import { useMemo, useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  getQuiz,
  updateQuiz,
  createQuiz,
  deleteQuiz,
  claimQuiz,
  type Quiz,
} from "@/lib/quiz-api";
import QuizForm from "@/components/quiz-form";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";

import { debounce } from "lodash";

interface EditQuizProps {
  params: { internalId: string };
  onQuizCreated?: (quiz: any) => void;
}

export default function EditQuiz({ params, onQuizCreated }: EditQuizProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user, signInWithGoogle } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [saveStatus, setSaveStatus] = useState<
    "saving" | "saved" | "unsaved" | null
  >(null);
  const [localQuiz, setLocalQuiz] = useState<Quiz | null>(null);
  const [lastSavedData, setLastSavedData] = useState<any>(null);

  // Fetch initial quiz data
  const { data: initialQuiz, isLoading } = useQuery<Quiz>({
    queryKey: [`/api/quiz/${params.internalId}`],
    queryFn: () => getQuiz(params.internalId),
    enabled: !!params.internalId,
    staleTime: Infinity, // Prevent automatic refetching
  });

  // Update quiz ownership when a logged-in user visits an unclaimed quiz
  useEffect(() => {
    if (!user || initialQuiz.creatorId) {
      return;
    }

    const attemptClaimQuiz = async () => {
      try {
        if (initialQuiz && params.internalId) {
          await claimQuiz(params.internalId);
          toast({
            title: "Quiz claimed",
            description: "This quiz is now saved to your account",
          });
        }
      } catch (error) {
        console.error("Failed to claim quiz:", error);
      }
    };

    if (user) {
      // Small delay to ensure token is saved to localStorage after login
      setTimeout(attemptClaimQuiz, 500);
    }
  }, [initialQuiz, user, params.internalId]);

  // Set initial local state
  useEffect(() => {
    if (initialQuiz && !localQuiz) {
      setLocalQuiz(initialQuiz);
      setLastSavedData(getFormDataFromQuiz(initialQuiz));
    }
  }, [initialQuiz]);

  const updateQuizMutation = useMutation({
    mutationFn: async (data: any) => {
      try {
        const title = `${data.wifeName} & ${data.husbandName}`;
        const quizData = {
          title,
          welcomeMessage: data.welcomeMessage,
          shareCode: data.shareCode,
          theme: data.theme,
          questions: data.questions.map((question: any) => ({
            text: question.text,
            correctAnswer: question.correctAnswer,
            optionA: data.wifeName,
            optionB: data.husbandName,
          })),
        };

        const updated = await updateQuiz(localQuiz!.internalId, quizData);
        setLastSavedData(data); // Update last saved data
        return updated;
      } catch (error: any) {
        console.error("Error updating quiz:", error);
        throw new Error(error.message || "Failed to update quiz");
      }
    },
    onSuccess: () => {
      setSaveStatus("saved");
    },
    onError: (error: Error) => {
      setSaveStatus("unsaved");
      toast({
        title: "Error saving changes",
        description:
          error.message ||
          "Failed to save changes. Your changes are still preserved locally.",
        variant: "destructive",
      });
    },
  });

  const createQuizMutation = useMutation({
    mutationFn: async (data: any) => {
      try {
        const title = `${data.wifeName} & ${data.husbandName}`;
        const quizData = {
          title,
          welcomeMessage: data.welcomeMessage,
          shareCode: data.shareCode,
          theme: data.theme,
          questions: data.questions.map((question: any) => ({
            text: question.text,
            correctAnswer: question.correctAnswer,
            optionA: data.wifeName,
            optionB: data.husbandName,
          })),
        };
        const createdQuiz = await createQuiz(quizData);
        return createdQuiz;
      } catch (error: any) {
        console.error("Error creating quiz:", error);
        throw new Error(error.message || "Failed to create quiz");
      }
    },
    onSuccess: (createdQuiz) => {
      setSaveStatus("saved");
      onQuizCreated && onQuizCreated(createdQuiz);
    },
    onError: (error: Error) => {
      setSaveStatus("unsaved");
      toast({
        title: "Error creating quiz",
        description: error.message || "Failed to create quiz.",
        variant: "destructive",
      });
    },
  });

  const deleteQuizMutation = useMutation({
    mutationFn: () => deleteQuiz(localQuiz!.internalId),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Quiz deleted successfully!",
      });
      setLocation("/quizzes");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete quiz",
        variant: "destructive",
      });
    },
  });

  // Debounced save function
  const debouncedUpdate = useMemo(
    () =>
      debounce(
        (data: any) => {
          // Only save if there are actual changes
          if (JSON.stringify(data) !== JSON.stringify(lastSavedData)) {
            setSaveStatus("saving");
            if (params.internalId) {
              updateQuizMutation.mutate(data);
            } else {
              createQuizMutation.mutate(data);
            }
          }
        },
        2000, // Increased debounce time to 2 seconds
        { leading: false, trailing: true },
      ),
    [lastSavedData],
  );

  const handleFormChange = (data: any) => {
    setSaveStatus("unsaved");
    // Update local state immediately
    if (localQuiz) {
      const title = `${data.wifeName} & ${data.husbandName}`;
      setLocalQuiz({
        ...localQuiz,
        title,
        welcomeMessage: data.welcomeMessage,
        shareCode: data.shareCode,
        theme: data.theme,
        questions: data.questions.map((question: any) => ({
          ...question,
          optionA: data.wifeName,
          optionB: data.husbandName,
        })),
      });
    } else {
      setLocalQuiz({
        title: `${data.wifeName} & ${data.husbandName}`,
        welcomeMessage: data.welcomeMessage,
        shareCode: data.shareCode,
        theme: data.theme,
        questions: data.questions.map((question: any) => ({
          text: question.text,
          correctAnswer: question.correctAnswer,
          optionA: data.wifeName,
          optionB: data.husbandName,
        })),
        internalId: null,
      });
    }
    // Schedule backend sync
    debouncedUpdate(data);
  };

  // Helper function to convert quiz to form data
  const getFormDataFromQuiz = (quiz: Quiz) => ({
    shareCode: quiz.shareCode,
    wifeName: quiz.questions[0]?.optionA || "",
    husbandName: quiz.questions[0]?.optionB || "",
    welcomeMessage: quiz.welcomeMessage,
    theme: quiz.theme || "", //Added to handle theme data
    questions: quiz.questions.map((q) => ({
      text: q.text,
      correctAnswer: q.correctAnswer,
    })),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!localQuiz && params.internalId) {
    return (
      <Card className="p-6 text-center">
        <p className="text-muted-foreground mb-4">Quiz not found</p>
        <Button onClick={() => setLocation("/quizzes")}>Back to Quizzes</Button>
      </Card>
    );
  }

  const formData = getFormDataFromQuiz(
    localQuiz || {
      shareCode: "",
      welcomeMessage: "",
      questions: [],
      internalId: null,
    },
  );

  if (!user && !params.internalId) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="p-6 space-y-6">
          <QuizForm
            initialData={formData}
            onSubmit={handleFormChange}
            autoSave
          />

          <div className="border-t pt-6">
            <p className="text-muted-foreground mb-4">
              Want to save this quiz later?
            </p>
            <Button
              variant="default"
              className="w-full bg-pink-500 hover:bg-pink-600"
              onClick={() => signInWithGoogle(localQuiz?.shareCode)}
            >
              Sign in to save it to your account
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        {params.internalId && (
          <p className="text-sm text-gray-500">
            {saveStatus === "saving"
              ? "Saving changes..."
              : saveStatus === "unsaved"
                ? "Unsaved changes"
                : "All changes saved"}
          </p>
        )}
        {params.internalId && (
          <Button
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
            disabled={deleteQuizMutation.isPending}
          >
            Delete Quiz
          </Button>
        )}
      </div>

      <QuizForm initialData={formData} onSubmit={handleFormChange} autoSave />
      {params.internalId && (
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the
                quiz and all its responses.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteQuizMutation.mutate()}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
